import React, { useEffect } from "react"
import { graphql } from "gatsby"
import { ReactSVG } from "react-svg"
import * as globalBlockStyles from "../main-blocks/main-blocks.module.scss"
import * as styles from "./via-transit-widget-block.module.scss"
import { infoTrafficBlockPicto, itineraryBlockPicto } from "../../../../static/images"

const VIA_TRANSIT_SDK_SCRIPT_ID = "via-transit-sdk-script"
const VIA_TRANSIT_SDK_URL = "https://link.viatransit.fr/fr/widget/data/viatransit-sdk.js"
const VIA_TRANSIT_PRODUCT_KEY = "mobivie-web"
const VIA_TRANSIT_LANG = "fr"

const formatLocale = (locale: string) => locale.toLowerCase().split("-").reverse().join("-")

type RenderProps = {
  vtType: string
  locale: string
  defaultLocale: string
  title?: string
  isMainBlock?: boolean
  isFullScreen?: boolean
  showMapPage?: boolean
  showSchedulesPage?: boolean
  showItinerariesPage?: boolean
  vtHeight?: number
  redirectPath?: string
}

const toVtBoolean = (value: boolean) => (value ? "true" : "false")

const ViaTransitWidgetBlock = ({
  vtType,
  locale,
  defaultLocale,
  title,
  isMainBlock = false,
  isFullScreen = false,
  showMapPage,
  showSchedulesPage,
  showItinerariesPage,
  vtHeight,
  redirectPath,
}: RenderProps) => {
  const formattedLocale = formatLocale(locale)
  const formattedDefaultLocale = formatLocale(defaultLocale)
  const defaultRedirectPath = "via-transit-search-results"
  const finalRedirectPath = redirectPath || defaultRedirectPath
  const computedRedirectPath =
    formattedLocale === formattedDefaultLocale ? `/${finalRedirectPath}` : `/${formattedLocale}/${finalRedirectPath}`
  const redirectUrl =
    typeof window !== "undefined" ? `${window.location.origin}${computedRedirectPath}` : computedRedirectPath

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    if (document.getElementById(VIA_TRANSIT_SDK_SCRIPT_ID)) {
      return
    }

    const script = document.createElement("script")
    script.id = VIA_TRANSIT_SDK_SCRIPT_ID
    script.async = true
    script.type = "module"
    script.src = VIA_TRANSIT_SDK_URL
    script.setAttribute("data-vt-product-key", VIA_TRANSIT_PRODUCT_KEY)
    script.setAttribute("data-vt-lang", VIA_TRANSIT_LANG)
    document.head.appendChild(script)
  }, [])

  const wrapperClassName = isFullScreen
    ? styles.fullScreen
    : `${styles.block} ${isMainBlock ? globalBlockStyles.mainBlocksCard : ""}`

  return (
    <section className={wrapperClassName}>
      <div className={styles.blockHeader}>
        {vtType === "traffic" ? (
          <ReactSVG
            src={infoTrafficBlockPicto}
            className={styles.blockPicto}
            beforeInjection={svg => svg.setAttribute("aria-hidden", "true")}
          />
        ) : (
          <img src={itineraryBlockPicto} className={styles.blockPicto} alt="" aria-hidden="true" />
        )}
        <h2 className={styles.title}>{title}</h2>
      </div>
      <div
        className={styles.widgetContainer}
        data-vt-type={vtType}
        data-vt-redirect-url={redirectUrl}
        data-vt-show-go-button="true"
        data-vt-page-map={showMapPage === undefined ? undefined : toVtBoolean(showMapPage)}
        data-vt-page-schedules={showSchedulesPage === undefined ? undefined : toVtBoolean(showSchedulesPage)}
        data-vt-page-itineraries={showItinerariesPage === undefined ? undefined : toVtBoolean(showItinerariesPage)}
        data-vt-height={vtHeight !== undefined ? String(vtHeight) : undefined}
        data-vt-hide-traffic-title={vtType === "traffic" ? "true" : undefined}
        data-vt-hide-traffic-subtitle={vtType === "traffic" ? "true" : undefined}
        data-vt-hide-logo="true"
      />
    </section>
  )
}

export default ViaTransitWidgetBlock

export const fragments = graphql`
  fragment ViaTransitWidgetBlockFields on DatoCmsViaTransitWidgetBlock {
    __typename
    id
    widgetType
  }
`
